/**
 * Which endings a cycle can actually have.
 *
 * The end-cycle modal used to offer the same five options to everyone:
 * pregnancy, no pregnancy, miscarriage, cancelled, other. Three of those are
 * meaningless on a preservation or donation cycle — nobody gets pregnant from
 * freezing eggs — and, worse, there was no way to say a preservation cycle simply
 * *finished*. A patient completing egg freezing had to file it as a pregnancy
 * result or an "other", and the app then showed them a results Home instead of
 * their storage Home.
 *
 * The families mirror `resolveCycleClosingContent` in
 * `@src/packages/homepage/closing/cycleClosingSlides`, so the options a patient
 * is offered and the words they are given afterwards are decided the same way.
 */

const PRESERVATION_CYCLE_TYPES: ReadonlySet<string> = new Set([
  "egg_freezing",
  "embryo_freezing",
  "sperm_freezing",
]);

export type EndCycleFamily = "conceiving" | "preservation" | "donation";

/**
 * The same outcome vocabulary is used in two different moments:
 *
 * - `past_cycle_outcome` is historical data entry, where a patient is telling
 *   us everything that happened after the cycle.
 * - `current_cycle_close` is a lifecycle action, where a patient is closing
 *   the treatment cycle that is open now.
 *
 * A pregnancy loss belongs to the first moment (or to the dedicated result
 * flow) rather than being offered as a new way to close a cycle whose positive
 * beta is already recorded.
 */
export type EndCycleContext = "current_cycle_close" | "past_cycle_outcome";

/**
 * The value an option writes to `outcome.type`.
 *
 * `completed` is not an API value: preservation completion is stored as a null
 * outcome type, which is how `useCompleteCycle` closes those cycles and how the
 * display layer infers completion from the cycle type. Keeping it as a distinct
 * option id lets the picker offer it without inventing a schema field.
 */
export type EndCycleOptionId =
  | "pregnancy"
  | "no_pregnancy"
  | "miscarriage"
  | "cancelled"
  | "other"
  | "completed";

export type EndCycleOption = {
  id: EndCycleOptionId;
  labelKey: string;
  descriptionKey: string;
};

export type EndCycleOptionsInput = {
  family: EndCycleFamily;
  context: EndCycleContext;
  /** True when a transfer or insemination has actually been completed. */
  hasPregnancyOpportunity?: boolean;
  /** A result already recorded by the beta/result flow, if there is one. */
  recordedOutcomeType?: Exclude<EndCycleOptionId, "completed"> | null;
  /** True when the target already has an outcome record to recap. */
  hasRecordedOutcome?: boolean;
};

const NS = "you.modals.end_cycle";

const PREGNANCY: EndCycleOption = {
  id: "pregnancy",
  labelKey: `${NS}.outcome_pregnancy`,
  descriptionKey: `${NS}.pregnancy_outcome_description`,
};
const NO_PREGNANCY: EndCycleOption = {
  id: "no_pregnancy",
  labelKey: `${NS}.outcome_no_pregnancy`,
  descriptionKey: `${NS}.no_pregnancy_outcome_description`,
};
const MISCARRIAGE: EndCycleOption = {
  id: "miscarriage",
  labelKey: `${NS}.outcome_miscarriage`,
  descriptionKey: `${NS}.pregnancy_loss_outcome_description`,
};
const CANCELLED: EndCycleOption = {
  id: "cancelled",
  labelKey: `${NS}.outcome_cancelled`,
  descriptionKey: `${NS}.cycle_cancelled_outcome_description`,
};
const OTHER: EndCycleOption = {
  id: "other",
  labelKey: `${NS}.outcome_other`,
  descriptionKey: `${NS}.other_outcome_description`,
};

/**
 * Donation is read from the goal, never from a pathway: a patient *using* donor
 * gametes is a recipient on a conceiving journey, while an actual donor has a
 * donation goal. Preservation is read from the cycle type.
 */
export function resolveEndCycleFamily({
  cycleType,
  treatmentGoal,
}: {
  cycleType: string | null | undefined;
  treatmentGoal: string | null | undefined;
}): EndCycleFamily {
  if (treatmentGoal === "egg-donation" || treatmentGoal === "sperm-donation") {
    return "donation";
  }
  if (cycleType && PRESERVATION_CYCLE_TYPES.has(cycleType)) {
    return "preservation";
  }
  return "conceiving";
}

type EndCycleOptionsResult = {
  options: EndCycleOption[];
  /** Copy key for the question above the list. */
  promptKey: string;
};

/**
 * The string overload keeps the old helper convenient for callers that only
 * need a family. It represents historical data entry, where the full outcome
 * vocabulary is intentionally available. Current-cycle closeout callers pass
 * the context object explicitly.
 */
export function resolveEndCycleOptions(
  family: EndCycleFamily,
): EndCycleOptionsResult;
export function resolveEndCycleOptions(
  input: EndCycleOptionsInput,
): EndCycleOptionsResult;
export function resolveEndCycleOptions(
  value: EndCycleFamily | EndCycleOptionsInput,
): EndCycleOptionsResult {
  return resolveEndCycleOptionsForContext(
    typeof value === "string"
      ? { family: value, context: "past_cycle_outcome" }
      : value,
  );
}

function resolveEndCycleOptionsForContext({
  family,
  context,
  hasPregnancyOpportunity = false,
  recordedOutcomeType = null,
  hasRecordedOutcome = false,
}: EndCycleOptionsInput): EndCycleOptionsResult {
  const isPastOutcome = context === "past_cycle_outcome";

  if (family === "preservation") {
    return {
      options: [
        {
          id: "completed",
          labelKey: `${NS}.outcome_completed_preservation`,
          descriptionKey: `${NS}.completed_preservation_description`,
        },
        CANCELLED,
        OTHER,
      ],
      promptKey: isPastOutcome
        ? `${NS}.past_preservation_outcome_prompt`
        : `${NS}.prompt_preservation`,
    };
  }

  if (family === "donation") {
    return {
      options: [
        {
          id: "completed",
          labelKey: `${NS}.outcome_completed_donation`,
          descriptionKey: `${NS}.completed_donation_description`,
        },
        CANCELLED,
        OTHER,
      ],
      promptKey: isPastOutcome
        ? `${NS}.past_donation_outcome_prompt`
        : `${NS}.prompt_donation`,
    };
  }

  if (isPastOutcome) {
    return {
      options: [PREGNANCY, NO_PREGNANCY, MISCARRIAGE, CANCELLED, OTHER],
      promptKey: `${NS}.past_outcome_prompt`,
    };
  }

  // Once a beta/result has been recorded, closing the treatment cycle should
  // confirm that result rather than ask the patient to reinterpret it. In
  // particular, a positive first beta is pregnancy at this step; a later loss
  // is a separate pregnancy event and not another current-cycle closeout
  // choice. Keep a previously recorded loss addressable so saving a close does
  // not require the modal to invent a different outcome.
  /**
   * `other` records no ending, so there is nothing here to confirm — and
   * narrowing to it made the close a one-way door. Reopening this modal on a
   * cycle already closed as `other` offered `[OTHER]` and nothing else, while
   * `RecordedResultView` is gated on an open cycle and `canUpdatePregnancy` on
   * `type === "pregnancy"`: a patient who picked it by mistake, over a positive
   * beta, had no way anywhere in the app to say so. Falling through gives her
   * the full vocabulary back.
   */
  if (
    hasRecordedOutcome &&
    recordedOutcomeType &&
    recordedOutcomeType !== "other"
  ) {
    const recordedOption = {
      pregnancy: PREGNANCY,
      no_pregnancy: NO_PREGNANCY,
      miscarriage: MISCARRIAGE,
      cancelled: CANCELLED,
    }[recordedOutcomeType];
    return {
      options: [recordedOption, OTHER],
      promptKey: `${NS}.current_close_recorded_result_prompt`,
    };
  }

  /**
   * She is correcting an ending already recorded as `other`.
   *
   * Falling through was not enough on its own: the fall-through hides the
   * pregnancy endings unless `hasPregnancyOpportunity`, and the modal infers that
   * flag from a transfer date or from the recorded outcome type — which this close
   * overwrote to `other`. So every caller without phase data (`PlanHomeScreen`,
   * `YouScreen`) still offered only cancelled and other, and the correction this
   * exists for stayed impossible. A recorded ending is itself the evidence that
   * this cycle got far enough to have one.
   */
  const isCorrectingAnUnnamedEnding =
    hasRecordedOutcome && recordedOutcomeType === "other";
  const offersPregnancyEndings =
    hasPregnancyOpportunity || isCorrectingAnUnnamedEnding;

  return {
    options: offersPregnancyEndings
      ? [PREGNANCY, NO_PREGNANCY, CANCELLED, OTHER]
      : [CANCELLED, OTHER],
    promptKey: offersPregnancyEndings
      ? `${NS}.current_close_after_procedure_prompt`
      : `${NS}.current_close_before_outcome_prompt`,
  };
}

/**
 * The `outcome.type` an option writes. `completed` maps to null — see
 * `EndCycleOptionId`.
 */
export function outcomeTypeForOption(
  id: EndCycleOptionId,
): "pregnancy" | "no_pregnancy" | "miscarriage" | "cancelled" | "other" | null {
  return id === "completed" ? null : id;
}
